import { ReactElement, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Field,
  Fieldset,
  Heading,
  Input,
  Label,
  Link,
  Paragraph,
  Radio,
  Skeleton,
  Tag,
  Textarea,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { DeviationView } from '../../../api/safetyRounds';
import { Routes } from '../../../enums/Routes';
import {
  useDeviationActionMutation,
  useDeviationQuery,
  useRemindDeviationMutation,
} from '../../../hooks/queries/safetyRounds/useDeviationQueries';
import { longDate } from '../Plans/plansView';
import { protocolPathFor } from '../Protocol/protocolView';
import { useGetUserinfo } from '../../../hooks/queries/useGetUserinfo';
import {
  type Choice,
  choiceErrors,
  choicesFor,
  type ChoiceError,
  DUE_COLORS,
  STATUS_COLORS,
} from './deviationView';
import layout from '../../HRDashboard/hmsPage.module.css';
import classes from './Deviations.module.css';

/** The same page for everyone in the case. What they can do follows their role in it. */
export const DeviationPage = (): ReactElement => {
  const { t } = useTranslation();
  const id = Number(useParams().deviationId);
  const { data, isLoading, error } = useDeviationQuery(id);

  if (isLoading)
    return (
      <div aria-busy="true" className={layout.loading}>
        <span className="ds-sr-only">{t('safetyRounds.deviation.loading')}</span>
        <Skeleton variant="rectangle" width="100%" height={420} />
      </div>
    );
  if (error || !data)
    return (
      <div className={layout.loading}>
        <Alert data-color="danger">
          <Heading level={2} data-size="2xs">
            {t('safetyRounds.deviation.notFoundTitle')}
          </Heading>
          <Paragraph>{t('safetyRounds.deviation.notFound')}</Paragraph>
        </Alert>
      </div>
    );
  return <Deviation deviation={data} />;
};

const STEPS = ['registered', 'handled', 'finished'] as const;

const Deviation = ({ deviation }: { deviation: DeviationView }): ReactElement => {
  const { t, i18n } = useTranslation();
  const K = 'safetyRounds.deviation';
  const remind = useRemindDeviationMutation(deviation.id);
  const roles = useGetUserinfo().data?.userRoles ?? [];

  return (
    <div className={layout.page} data-size="md">
      <div className={classes.content}>
        <Link asChild data-size="sm" className={layout.startAligned}>
          <RouterLink to={deviation.isResponsible ? Routes.MyDeviations : protocolPathFor(deviation.roundId, roles)}>
            {deviation.isResponsible ? t(`${K}.backToMine`) : t(`${K}.backToProtocol`)}
          </RouterLink>
        </Link>
        <header className={classes.header}>
          <span className={layout.eyebrow}>{t(`${K}.eyebrow`)}</span>
          <Heading level={1} data-size="lg">
            {deviation.number} {deviation.title}
          </Heading>
          <Paragraph data-size="sm" className={layout.subtle}>
            {[deviation.plan.name, deviation.plan.area].join(' · ')}
          </Paragraph>
          <div className={classes.tags}>
            <Tag data-color={STATUS_COLORS[deviation.status]} data-size="sm">
              {t(`safetyRounds.protocol.deviationStatus.${deviation.status}`)}
            </Tag>
          </div>
        </header>

        {deviation.released === false && (
          <Alert data-color="info" data-size="sm">
            <Paragraph data-size="sm">{t(`${K}.notReleased`)}</Paragraph>
          </Alert>
        )}

        {deviation.confirmedOwnFix && (
          <Alert data-color="info" data-size="sm">
            <Paragraph data-size="sm">{t(`${K}.confirmedOwnFix`)}</Paragraph>
          </Alert>
        )}

        <ol className={classes.steps} aria-label={t(`${K}.progress`)}>
          {STEPS.map((step, index) => {
            const number = index + 1;
            const state = number < deviation.step ? 'done' : number === deviation.step ? 'current' : 'next';
            return (
              <li key={step} data-state={state} aria-current={state === 'current' ? 'step' : undefined}>
                <span className={classes.stepNumber} aria-hidden="true">
                  {number}
                </span>
                {t(`${K}.steps.${step}.${deviation.status}`, { defaultValue: t(`${K}.steps.${step}.default`) })}
              </li>
            );
          })}
        </ol>

        <section aria-labelledby="deviation-facts" className={layout.card} data-gap="md">
          <Heading level={2} data-size="xs" id="deviation-facts" className="ds-sr-only">
            {t(`${K}.facts`)}
          </Heading>
          <div>
            <span className="ds-label" data-size="sm">
              {t(`${K}.description`)}
            </span>
            <Paragraph data-size="sm">{deviation.description}</Paragraph>
          </div>
          <dl className={classes.facts}>
            <div>
              <dt>{t(`${K}.responsible`)}</dt>
              <dd>{deviation.responsibleEmail ?? '–'}</dd>
            </div>
            <div>
              <dt>{t(`${K}.due`)}</dt>
              <dd>
                <Tag data-color={DUE_COLORS[deviation.dueState]} data-size="sm">
                  {longDate(deviation.dueDate, i18n.language)}
                  {deviation.dueState === 'overdue' && ` · ${t(`${K}.overdue`)}`}
                </Tag>
              </dd>
            </div>
            <div>
              <dt>{t(`${K}.found`)}</dt>
              <dd>{longDate(deviation.foundOn, i18n.language)}</dd>
            </div>
          </dl>
          {deviation.actionTaken && (
            <div className={classes.done}>
              <span className="ds-label" data-size="sm">
                {t(`${K}.actionTaken`)}
              </span>
              <Paragraph data-size="sm">{deviation.actionTaken}</Paragraph>
            </div>
          )}
        </section>

        {/* A new step gives new choices, so a choice from the step before, such as «Hva er gjort?», is not left open */}
        <Actions key={`${deviation.status}:${deviation.actions.join(',')}`} deviation={deviation} />

        {deviation.actions.includes('remind') && (
          <div className={layout.actions}>
            <Button data-size="sm" variant="secondary" aria-busy={remind.isPending || undefined} onClick={() => remind.mutate()}>
              {t(`${K}.remind`)}
            </Button>
          </div>
        )}
        {remind.isSuccess && (
          <Paragraph data-size="sm" role="status">
            {t(`${K}.reminded`)}
          </Paragraph>
        )}

        <section aria-labelledby="deviation-history" className={classes.section}>
          <Heading level={2} data-size="sm" id="deviation-history">
            {t(`${K}.history`)}
          </Heading>
          <ol className={classes.history}>
            {deviation.history.map((event, index) => (
              <li key={index}>
                <span className="ds-label" data-size="sm">
                  {t(`${K}.events.${event.type}`)}
                </span>
                <Paragraph data-size="xs" className={layout.subtle}>
                  {[longDate(event.at, i18n.language), event.actorEmail].filter(Boolean).join(' · ')}
                </Paragraph>
                {event.type === 'DEADLINE_MOVED' && event.fromDueDate && event.toDueDate && (
                  <Paragraph data-size="sm">
                    {t(`${K}.moved`, {
                      from: longDate(event.fromDueDate, i18n.language),
                      to: longDate(event.toDueDate, i18n.language),
                    })}
                  </Paragraph>
                )}
                {event.ownFix && <Paragraph data-size="sm">{t(`${K}.ownFixEvent`)}</Paragraph>}
                {event.comment && <Paragraph data-size="sm">{event.comment}</Paragraph>}
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
};

/** The choices of the user's role, with the text each one requires */
const Actions = ({ deviation }: { deviation: DeviationView }): ReactElement | null => {
  const { t } = useTranslation();
  const K = 'safetyRounds.deviation';
  const choices = choicesFor(deviation.actions);
  const [choice, setChoice] = useState<Choice | null>(choices.length === 1 ? choices[0] : null);
  const [comment, setComment] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [errors, setErrors] = useState<ChoiceError[] | null>(null);
  const act = useDeviationActionMutation(deviation.id);

  if (choices.length === 0) return null;
  const group = choices.includes('markFixed') ? 'responsible' : choices.includes('confirmFixed') ? 'check' : 'decision';
  const errorFor = (field: ChoiceError['field']) => errors?.find((error) => error.field === field)?.message;

  const submit = () => {
    if (!choice) return;
    const found = choiceErrors(choice, { comment, dueDate }, deviation.dueDate, t);
    setErrors(found);
    if (found.length > 0) return;
    act.mutate(
      { action: choice, input: { comment: comment.trim(), ...(choice === 'moveDeadline' ? { dueDate } : {}) } },
      {
        onSuccess: () => {
          setComment('');
          setDueDate('');
          setErrors(null);
        },
      },
    );
  };

  return (
    <section aria-labelledby="deviation-actions" className={layout.card} data-gap="md" data-highlight="true">
      <Heading level={2} data-size="sm" id="deviation-actions">
        {t(`${K}.actionsTitle.${group}`)}
      </Heading>
      {group === 'check' && deviation.isResponsible && (
        <Paragraph data-size="sm">{t(`${K}.confirmingOwnFix`)}</Paragraph>
      )}
      {choices.length > 1 && (
        <Fieldset>
          <Fieldset.Legend className="ds-sr-only">{t(`${K}.actionsTitle.${group}`)}</Fieldset.Legend>
          {choices.map((option) => (
            <Radio
              key={option}
              name="deviation-choice"
              value={option}
              label={t(`${K}.choices.${option}.label`)}
              description={t(`${K}.choices.${option}.description`)}
              checked={choice === option}
              onChange={() => {
                setChoice(option);
                setErrors(null);
              }}
            />
          ))}
        </Fieldset>
      )}
      {choice && (
        <div className={classes.choice}>
          {choice === 'moveDeadline' && (
            <Field>
              <Label htmlFor="deviation-due">{t(`${K}.newDue`)}</Label>
              <Input
                id="deviation-due"
                type="date"
                value={dueDate}
                aria-invalid={errorFor('dueDate') ? true : undefined}
                onChange={(event) => setDueDate(event.target.value)}
              />
              {errorFor('dueDate') && <ValidationMessage>{errorFor('dueDate')}</ValidationMessage>}
            </Field>
          )}
          <Field>
            <Label htmlFor="deviation-comment">{t(`${K}.choices.${choice}.field`)}</Label>
            <Textarea
              id="deviation-comment"
              rows={3}
              value={comment}
              aria-invalid={errorFor('comment') ? true : undefined}
              onChange={(event) => setComment(event.target.value)}
            />
            {errorFor('comment') && <ValidationMessage>{errorFor('comment')}</ValidationMessage>}
          </Field>
          {act.isError && (
            <Alert data-color="danger" data-size="sm">
              <Paragraph data-size="sm">{t(`${K}.failed`)}</Paragraph>
            </Alert>
          )}
          <div className={layout.actions}>
            <Button data-size="sm" aria-busy={act.isPending || undefined} onClick={submit}>
              {t(`${K}.choices.${choice}.submit`)}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};
